import { Controller } from "@hotwired/stimulus"

// CP2020 armor-STATE readout — derived armor state on the show sheet (EV + SP by location,
// later damage/degradation). A PULL aggregator (mirrors cp2020--equipment-costs /
// cp2020--computed-stats): it OWNS *when* to compute and ASKS the armor collection for its
// derived values via an outlet. The armor collection owns the math; this holds none.
// recompute() is FUNCTIONAL — every value derived fresh from the currently-active armor.
//
// Recomputes on: load, the armor editor connecting, cp2020:active-applied (cold-load worn
// paint) and cp2020:play-state-changed (a worn toggle). Locations with no data render 0; an
// INVALID loadout (the sorter refused) renders EV/SP as "—" (can't compute an illegal config).
const LOCATIONS = ["head", "torso", "rarm", "larm", "rleg", "lleg"]

export default class extends Controller {
  static targets = ["ev", ...LOCATIONS]
  static outlets = ["cp2020--armor"]

  connect() {
    this.recompute()
  }

  // The armor editor connecting is a readiness signal — recompute so initial state lands.
  cp2020ArmorOutletConnected() { this.recompute() }

  recompute() {
    if (!this.hasCp2020ArmorOutlet) return
    this._renderEV(this.cp2020ArmorOutlet.ev())
    this._renderSP(this.cp2020ArmorOutlet.spByLocation())
  }

  // EV: { ev: Number, invalid } — invalid renders "—" (the loadout is illegal, no EV exists).
  _renderEV(result) {
    if (!this.hasEvTarget) return
    this.evTarget.textContent = result?.invalid ? "—" : String(result?.ev ?? 0)
  }

  // SP: { byLocation: { loc: n }, invalid } — invalid renders "—" per location (same illegal-
  // loadout rule as EV; you can't quote SP for a config the rules forbid).
  _renderSP(result) {
    const byLoc = result?.byLocation || {}
    for (const loc of LOCATIONS) {
      if (!this[`has${cap(loc)}Target`]) continue
      this[`${loc}Target`].textContent = result?.invalid ? "—" : String(byLoc[loc] || 0)
    }
  }
}

const cap = (s) => s[0].toUpperCase() + s.slice(1)
